/**
 * RestoID — MFA email verification code.
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream MFA code logic (`useMfaFlowState`,
 * `useMfaCodeVerification`, `useResendMfaVerificationCode`, `useTrustedDeviceOptIn`),
 * restated in `custom/components/OtpEntry.tsx` as `MfaCodeEntry` with the markup
 * swapped and nothing else. Both upstream guards are kept verbatim, so an invalid
 * session and a missing verification ID still land on the same ErrorPage with the
 * same `rawMessage`. Swapped in for `./pages/MfaVerification/EmailVerificationCode`.
 *
 * Deliberate differences from upstream:
 *   - Logto's SecondaryPageLayout + SectionLayout print the screen name TWICE
 *     ("Двухфакторная аутентификация" over "Введите код подтверждения…"); RestoID
 *     shows one ScreenHead with the section title, and keeps the layout title as the
 *     document title in `pageMeta`, exactly where upstream put it.
 *   - `mfa.enter_email_verification_code_description` ("2-step authentication has
 *     been enabled for this account. Please enter the code sent to X.") is replaced
 *     by the design's "code sent to …" chip. The load-bearing half of that sentence —
 *     the masked identifier — is what the chip shows; the boilerplate half is
 *     dropped. No chip is rendered when the server sent no masked identifier, rather
 *     than upstream's dangling "sent to ." sentence.
 *   - The NavBar back arrow becomes the ScreenHead back pill (same `navigate(-1)`),
 *     and `SwitchMfaFactorsLink` becomes the shared RestoID `MfaSwitchLink`, which
 *     applies the same "fewer than two factors → render nothing" rule.
 */
import { MfaFactor, SignInIdentifier } from '@logto/schemas';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useMfaFlowState from '@/hooks/use-mfa-factors-state';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { UserMfaFlow } from '@/types';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import { MfaCodeEntry, SentToChip } from '../components/OtpEntry';
import { ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const EmailVerificationCode = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const flowState = useMfaFlowState();
  const { verificationIdsMap } = useContext(UserInteractionContext);

  if (!flowState) {
    return <ErrorPage title="error.invalid_session" />;
  }

  // VerificationId not found
  const verificationId = verificationIdsMap[codeVerificationTypeMap[SignInIdentifier.Email]];
  if (!verificationId) {
    return <ErrorPage title="error.invalid_session" rawMessage="Verification ID not found" />;
  }

  const maskedEmail = flowState.maskedIdentifiers?.[MfaFactor.EmailVerificationCode];

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.verify_mfa_factors' }}>
      <Stack gap={22}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('mfa.enter_email_verification_code')}
        />
        {maskedEmail ? <SentToChip target={maskedEmail} /> : null}
        <MfaCodeEntry identifierType={SignInIdentifier.Email} verificationId={verificationId} />
        <MfaSwitchLink flow={UserMfaFlow.MfaVerification} flowState={flowState} />
      </Stack>
    </FirstScreenLayout>
  );
};

export default EmailVerificationCode;
